import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from './lib/api.js';
import { useAuth } from './lib/auth.jsx';
import { ThemeProvider, useTheme } from './lib/theme.jsx';
import { OverlayProvider, useOverlay } from './lib/overlay.jsx';
import { NAV_GROUPS, ROLE_LABEL } from './lib/nav.js';
import { WorkspaceViewProvider, useWorkspaceView, ADMIN_VIEW_NAV_IDS, WORKSPACE_VIEW_LABELS } from './lib/workspaceView.jsx';
import WorkspaceViewSwitcher from './components/WorkspaceViewSwitcher.jsx';
import { LOGO } from './lib/logo.js';
import { Icon } from './lib/icons.jsx';
import { Avatar, Loading, Insight } from './components/ui.jsx';
import { ChangePasswordForm } from './components/PasswordForms.jsx';
import CommandPalette from './components/CommandPalette.jsx';
import Assistant from './components/Assistant.jsx';
import AppearancePanel from './components/Appearance.jsx';
import { useActions } from './components/actions.jsx';

import Login from './pages/Login.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import Dashboard from './pages/Dashboard.jsx';

/**
 * Everything past the dashboard is fetched when it is first opened.
 *
 * The whole application used to arrive as one 692KB file before anybody could
 * sign in, including the org chart, the payroll screens and the audit trail —
 * pages most people never open. Login and the dashboard stay eager because they
 * are, in order, the first thing a visitor sees and the first thing a signed-in
 * one does; making those wait on a second request would trade a real delay for
 * a theoretical one.
 */
const MySpace = lazy(() => import('./pages/MySpace.jsx'));
const Directory = lazy(() => import('./pages/Directory.jsx'));
const Profile = lazy(() => import('./pages/Profile.jsx'));
const OrgChart = lazy(() => import('./pages/OrgChart.jsx'));
const Onboarding = lazy(() => import('./pages/Onboarding.jsx'));
const Offboarding = lazy(() => import('./pages/Offboarding.jsx'));
const Documents = lazy(() => import('./pages/Documents.jsx'));
const Attendance = lazy(() => import('./pages/Attendance.jsx'));
const Biometric = lazy(() => import('./pages/Biometric.jsx'));
const Leave = lazy(() => import('./pages/Leave.jsx'));
const Holidays = lazy(() => import('./pages/Holidays.jsx'));
const Performance = lazy(() => import('./pages/Performance.jsx'));
const Payroll = lazy(() => import('./pages/Payroll.jsx'));
const Announcements = lazy(() => import('./pages/Announcements.jsx'));
const Helpdesk = lazy(() => import('./pages/Helpdesk.jsx'));
const Insights = lazy(() => import('./pages/Insights.jsx'));
const Reports = lazy(() => import('./pages/Reports.jsx'));
const Audit = lazy(() => import('./pages/Audit.jsx'));
const Security = lazy(() => import('./pages/Security.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));

/* ----------------------------------------------------------------- side rail */

function Sidebar({ collapsed, badges, onOpenAssistant }) {
  const { can } = useAuth();
  const { workspaceView } = useWorkspaceView();

  return (
    <aside className="sidebar">
      <NavLink to="/" className="brand" style={{ color: 'inherit' }}>
        <div className="brand-mark"><img src={LOGO} alt="SevenMentor" /></div>
        <div className="brand-txt">
          <div className="brand-name">SevenMentor</div>
          <div className="brand-sub">
            {workspaceView === 'admin' ? 'Admin · systems & security' : 'Smarter HR. Stronger Teams.'}
          </div>
        </div>
      </NavLink>

      <nav className="nav">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((i) => {
            if (i.permission && !can(i.permission)) return false;
            if (workspaceView === 'admin' && !ADMIN_VIEW_NAV_IDS.has(i.id)) return false;
            return true;
          });
          if (!items.length) return null;
          return (
            <div key={group.group}>
              <div className="nav-group-t">{group.group}</div>
              {items.map((item) => {
                const count = item.badge ? badges[item.badge] : 0;
                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon name={item.icon} strokeWidth={1.8} />
                    <span className="nav-label">{item.label}</span>
                    {count > 0 && <span className={`nav-badge${item.badgeSoft ? ' soft' : ''}`}>{count}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="side-foot">
        <div className="side-card">
          <h5>AI HR Assistant</h5>
          <p>Ask about leave, payroll or policies in plain language.</p>
          <button type="button" onClick={onOpenAssistant}>Open assistant</button>
        </div>
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------- role switcher */

/**
 * Anybody on the roll, searched by name, code or email.
 *
 * This used to be five hard-coded demo accounts signed into with a shared
 * password, which stopped working the moment the demo data was cleared and
 * never did answer the actual question: an administrator needs to see what a
 * particular person sees, not what one of five specimen roles sees.
 */
function ViewAsPicker({ onPicked }) {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);

  const people = useQuery({
    queryKey: ['employees', 'view-as', search],
    queryFn: () => get('/employees', { q: search || undefined, pageSize: 200 }),
  });

  // Looking at your own account through impersonation would only take away your
  // ability to write; somebody who has left has no view worth inspecting.
  const rows = (people.data?.data ?? []).filter((p) => p.id !== user.id && p.status !== 'EXITED');
  const total = people.data?.meta?.total ?? rows.length;

  return (
    <>
      <input
        className="inp" autoFocus placeholder="Search by name, employee code or email…"
        value={search} onChange={(e) => setSearch(e.target.value)}
      />
      <div style={{ maxHeight: 320, overflowY: 'auto', margin: '14px -4px 0' }}>
        {people.isLoading ? <Loading /> : rows.length === 0 ? (
          <div className="txt-sm muted" style={{ padding: '10px 4px' }}>
            {search ? 'Nobody matches that.' : 'There is nobody else on the roll yet.'}
          </div>
        ) : (
          <>
            {rows.map((p) => (
              <button
                key={p.id}
                type="button"
                className="check-item"
                disabled={busy !== null}
                style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                onClick={async () => { setBusy(p.id); try { await onPicked(p); } finally { setBusy(null); } }}
              >
                <div style={{ flex: 1 }}>
                  <div className="ct">{p.fullName}</div>
                  <div className="cs">
                    {p.employeeCode} · {ROLE_LABEL[p.role] ?? p.role}
                    {p.designation ? ` · ${p.designation}` : ''}
                  </div>
                </div>
                {busy === p.id && <span className="txt-sm muted">Switching…</span>}
              </button>
            ))}
            {total > rows.length + 1 && (
              <div className="txt-sm muted" style={{ padding: '10px 4px' }}>
                Showing {rows.length} of {total}. Type a name to narrow the list.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function RoleSwitcher() {
  const { user, can, viewAs } = useAuth();
  const { workspaceView, canSwitchWorkspaceView } = useWorkspaceView();
  const { openModal, closeModal, toast } = useOverlay();
  const navigate = useNavigate();

  const open = () => openModal({
    title: 'View as another user',
    subtitle: 'See the application exactly as they see it — read-only, and recorded against you',
    size: 'wide',
    body: () => (
      <ViewAsPicker
        onPicked={async (person) => {
          try {
            await viewAs(person);
            closeModal();
            navigate('/');
            toast(`Viewing as ${person.fullName} — read-only`, 'warn');
          } catch (err) {
            toast(err?.message ?? 'Could not switch to that account', 'crit');
          }
        }}
      />
    ),
  });

  const viewLabel = canSwitchWorkspaceView
    ? (WORKSPACE_VIEW_LABELS[workspaceView] || workspaceView)
    : (ROLE_LABEL[user.role] ?? user.role);

  // Everyone sees which desk they hold; only somebody who may impersonate gets
  // a control, rather than a button that opens a dialog they cannot act on.
  if (!can('security.manage')) {
    return (
      <span className="role-pill" title="The permission scope you are working in">
        <span className="rdot" />
        {viewLabel} view
      </span>
    );
  }

  return (
    <button type="button" className="role-pill" onClick={open} title="View the application as another user">
      <span className="rdot" />
      {viewLabel} view
      <Icon name="chevD" />
    </button>
  );
}

/* ------------------------------------------------------------- account menu */

/**
 * The account menu, and the only place Sign out lives.
 *
 * It was previously an unlabelled icon in a row of other unlabelled icons, which
 * is a fine way to hide the one control every employee eventually needs. Now the
 * trigger carries the person's name and the menu spells the action out.
 */
/**
 * A band across the top while an administrator is looking as somebody else.
 *
 * Deliberately loud and impossible to dismiss. The failure this prevents is an
 * administrator forgetting which account they are looking at and reporting a
 * bug — or worse, a policy decision — based on a view that was never theirs.
 * It also says read-only, because the first thing anybody tries is a change
 * and a bare 403 would read as a broken permission.
 */
function ViewingAsBanner() {
  const { viewingAs, stopViewingAs } = useAuth();
  const [leaving, setLeaving] = useState(false);
  if (!viewingAs) return null;

  const stop = async () => {
    setLeaving(true);
    try { await stopViewingAs(); } finally { setLeaving(false); }
  };

  return (
    <div className="viewas" role="status">
      <Icon name="eye" />
      <span>
        Viewing as <b>{viewingAs.fullName}</b> · {ROLE_LABEL[viewingAs.role] ?? viewingAs.role}
        <span className="viewas-ro"> — read-only</span>
      </span>
      <button type="button" className="viewas-exit" onClick={stop} disabled={leaving}>
        {leaving ? 'Returning…' : 'Return to my account'}
      </button>
    </div>
  );
}

function AccountMenu({ onAppearance }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useOverlay();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const go = (fn) => { setOpen(false); fn(); };

  return (
    <div className="acct" ref={ref}>
      <button
        type="button"
        className="role-pill"
        style={{ paddingLeft: 5 }}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={user.fullName} size="sm" employeeId={user.id} hasPhoto={Boolean(user.photoUrl)} />
        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.firstName}
        </span>
        <Icon name="chevD" />
      </button>

      {open && (
        <div className="acct-menu" role="menu">
          <div className="acct-head">
            <Avatar name={user.fullName} size="md" employeeId={user.id} hasPhoto={Boolean(user.photoUrl)} />
            <div style={{ minWidth: 0 }}>
              <div className="acct-name">{user.fullName}</div>
              <div className="acct-mail">{user.email}</div>
              <div className="txt-sm muted mt-8">{ROLE_LABEL[user.role] ?? user.role} · {user.employeeCode}</div>
            </div>
          </div>

          <button type="button" className="acct-item" role="menuitem" onClick={() => go(() => navigate('/me'))}>
            <Icon name="user" />My profile
          </button>
          <button type="button" className="acct-item" role="menuitem" onClick={() => go(() => navigate('/payroll'))}>
            <Icon name="wallet" />My payslips
          </button>
          <button type="button" className="acct-item" role="menuitem" onClick={() => go(onAppearance)}>
            <Icon name="palette" />Appearance
          </button>
          <button type="button" className="acct-item" role="menuitem" onClick={() => go(() => openModal({
              title: 'Change your password',
              subtitle: 'You will be signed out everywhere else',
              body: ({ close }) => <ChangePasswordForm close={close} />,
            }))}>
            <Icon name="shield" />Change password
          </button>

          <div className="acct-sep" />

          <button
            type="button"
            className="acct-item danger"
            role="menuitem"
            onClick={() => go(() => signOut().then(() => navigate('/login')))}
          >
            <Icon name="logout" />Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- notifications */

/**
 * The bell.
 *
 * Two different things share this panel, and they are not the same kind of fact.
 * Notifications are events: they were written when something happened and they
 * still say the same thing afterwards, so an approval from last Tuesday is still
 * there to read. The counts below them are live state — what is outstanding right
 * now — and they disappear on their own once the queue empties.
 *
 * Opening the panel marks the events read. Not the counts: those are not
 * something you can finish reading, only something you can act on.
 */
function NotificationsButton({ badges, anomalies, onOpen }) {
  const { openModal, closeModal } = useOverlay();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const feed = useQuery({
    queryKey: ['notifications'],
    queryFn: () => get('/notifications', { pageSize: 30 }),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: () => post('/notifications/read', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const events = feed.data?.data ?? [];
  const unread = feed.data?.meta?.unread ?? 0;

  const outstanding = [
    badges.leave > 0 && { icon: 'plane', tone: 'warn', title: `${badges.leave} leave requests waiting on you`, detail: 'Approvals queue in your scope', to: '/leave' },
    badges.helpdesk > 0 && { icon: 'ticket', tone: 'info', title: `${badges.helpdesk} open help desk tickets`, detail: 'Assigned across HR, IT and Finance', to: '/helpdesk' },
    badges.onboarding > 0 && { icon: 'userPlus', tone: 'info', title: `${badges.onboarding} people in onboarding`, detail: 'Checklists in progress', to: '/onboarding' },
    ...(anomalies ?? []).slice(0, 4).map((a) => ({
      icon: a.category === 'Attendance' ? 'fingerprint' : a.category === 'Payroll' ? 'wallet' : 'alert',
      tone: a.severity === 'CRITICAL' ? 'crit' : a.severity === 'WARNING' ? 'warn' : 'info',
      title: a.title,
      detail: a.detail,
      to: '/insights',
    })),
  ].filter(Boolean);

  const row = (n, key, isUnread = false) => (
    <button
      key={key}
      type="button"
      className="insight pointer"
      style={{ width: '100%', textAlign: 'left', ...(isUnread ? { background: 'var(--brand-wash)' } : {}) }}
      onClick={() => { closeModal(); if (n.link ?? n.to) navigate(n.link ?? n.to); }}
    >
      <div className="ins-ico" style={{ background: `var(--${n.tone}-bg)`, color: `var(--${n.tone})` }}>
        <Icon name={n.icon} />
      </div>
      <div>
        <div className="ins-t">{n.title}</div>
        <div className="ins-d">{n.detail}</div>
      </div>
    </button>
  );

  const open = () => {
    // Tells the shell to start fetching anomalies, which are only shown here.
    onOpen?.();
    openModal({
      title: 'Notifications',
      subtitle: unread > 0
        ? `${unread} new`
        : (events.length || outstanding.length ? 'Nothing new' : 'Nothing needs your attention'),
      body: () => (
        <div style={{ margin: -8 }}>
          {events.length === 0 && outstanding.length === 0 && (
            <div className="txt-sm muted">You are all caught up.</div>
          )}

          {events.map((n) => row(n, n.id, !n.isRead))}

          {outstanding.length > 0 && (
            <>
              {events.length > 0 && (
                <div className="txt-sm muted" style={{ padding: '12px 8px 6px', fontWeight: 600 }}>
                  Waiting on you
                </div>
              )}
              {outstanding.map((n, i) => row(n, `o${i}`))}
            </>
          )}
        </div>
      ),
    });

    // Opening is reading. Done after the modal so the highlight on unread rows
    // is still visible in the panel that was just opened.
    if (unread > 0) markRead.mutate();
  };

  return (
    <button
      type="button"
      className="icon-btn"
      onClick={open}
      title={unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
    >
      <Icon name="bell" />
      {unread > 0 && (
        <span className="count">{unread > 99 ? '99+' : unread}</span>
      )}
    </button>
  );
}

/* ---------------------------------------------------------------------- shell */

function Shell({ children }) {
  const { user, signOut, can } = useAuth();
  const qc = useQueryClient();
  const { appearance, update } = useTheme();
  const { openModal } = useOverlay();
  const navigate = useNavigate();
  const actions = useActions();

  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const dark = document.documentElement.getAttribute('data-theme') === 'dark';

  // Drop cached boards when identity changes (login, logout, or View as).
  // Otherwise a manager can briefly inherit an HR org-wide attendance cache.
  useEffect(() => {
    qc.clear();
  }, [user?.id, qc]);

  /**
   * Sidebar counts, in one request.
   *
   * This was three: up to a hundred pending leave requests, a hundred open
   * tickets and every onboarding checklist, fetched on every page, to render
   * three numbers. The server counts now and returns them together, so the
   * shell costs a few hundred bytes instead of a few hundred records.
   *
   * Each count is still gated server-side on the permission that would let you
   * act on it, so an employee's answer is three zeroes rather than a refusal.
   */
  const badgeQuery = useQuery({
    queryKey: ['badges', user?.id],
    queryFn: () => get('/meta/badges'),
    // Live sidebar counts: cheap COUNT queries; poll while the app is open.
    // Mutations still invalidate immediately for the actor.
    staleTime: 0,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    enabled: Boolean(user?.id),
  });
  const badges = badgeQuery.data?.data ?? { leave: 0, helpdesk: 0, onboarding: 0 };

  // Only when the panel is opened. It was the slowest call in the shell at
  // 75ms, fetched on every page load, to fill a list nobody had asked to see.
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const anomalies = useQuery({
    queryKey: ['anomalies'],
    queryFn: () => get('/insights/anomalies'),
    enabled: notificationsOpen && can('insights.read'),
  });

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openAppearance = () => openModal({
    title: 'Make it yours',
    subtitle: `Appearance settings for ${user.firstName} · saved to this account`,
    icon: 'palette',
    size: 'wide',
    body: () => <AppearancePanel name={user.fullName} />,
    footer: ({ close }) => (
      <>
        <button
          type="button"
          className="btn"
          onClick={() => update({ accent: 'sevenmentor', mode: 'light', density: 'comfortable', navStyle: 'tinted' })}
        >
          <Icon name="refresh" />Reset to company default
        </button>
        <button type="button" className="btn btn-primary" onClick={close}><Icon name="check" />Done</button>
      </>
    ),
  });

  const runAction = (id) => {
    if (id === 'leave') actions.applyLeave();
    else if (id === 'ticket') actions.raiseTicket();
    else if (id === 'assistant') setAssistantOpen(true);
  };

  return (
    <div className={`app${collapsed ? ' collapsed' : ''}`}>
      <ViewingAsBanner />
      <Sidebar collapsed={collapsed} badges={badges} onOpenAssistant={() => setAssistantOpen(true)} />

      <div className="main">
        <header className="topbar">
          <button type="button" className="icon-btn" onClick={() => setCollapsed((c) => !c)} title="Toggle sidebar" aria-label="Toggle sidebar">
            <Icon name="menu" />
          </button>

          <button type="button" className="searchbar" onClick={() => setPaletteOpen(true)} style={{ textAlign: 'left' }}>
            <Icon name="search" />
            <span>Search employees, pages, actions</span>
            <span className="kbd">⌘K</span>
          </button>

          <WorkspaceViewSwitcher />

          <div className="topbar-right">
            <button type="button" className="icon-btn" onClick={openAppearance} title="Appearance & theme" aria-label="Appearance">
              <Icon name="palette" />
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => update({ mode: dark ? 'light' : 'dark' })}
              title={dark ? 'Switch to light' : 'Switch to dark'}
              aria-label="Toggle dark mode"
            >
              <Icon name={dark ? 'sun' : 'moon'} />
            </button>

            <NotificationsButton
              badges={badges}
              anomalies={anomalies.data?.data}
              onOpen={() => setNotificationsOpen(true)}
            />
            {import.meta.env.DEV && <RoleSwitcher />}

            <AccountMenu onAppearance={openAppearance} />
          </div>
        </header>

        <div className="content">{children}</div>
      </div>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} onAction={runAction} />}

      <Assistant
        open={assistantOpen}
        onOpen={() => setAssistantOpen(true)}
        onClose={() => setAssistantOpen(false)}
        onRaiseTicket={() => { setAssistantOpen(false); actions.raiseTicket(); }}
      />
    </div>
  );
}

/* --------------------------------------------------------------------- routes */

export default function App() {
  const { status, user } = useAuth();

  if (status === 'loading') {
    return <div className="center" style={{ height: '100vh' }}><Loading label="Checking your session…" /></div>;
  }

  if (status === 'anonymous') {
    return (
      <ThemeProvider>
        <OverlayProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Must sit above the catch-all: somebody following a reset link
                has no session, so the redirect would eat the token. */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </OverlayProvider>
      </ThemeProvider>
    );
  }

  return (
    // The employee's saved appearance seeds the provider, so the theme is right
    // on the first paint rather than flashing the default first.
    //
    // The key matters more than it looks. Both branches of this component return
    // a <ThemeProvider> in the same position, so without it React reuses the
    // instance across the anonymous -> authenticated transition and never re-runs
    // the state initialiser — the saved theme would be ignored on sign-in and
    // only appear after a full reload. Keying by employee also gives the role
    // switcher a fresh provider when it signs in as a different account.
    <ThemeProvider key={user?.id ?? 'anon'} initial={user?.appearance}>
      <OverlayProvider>
        <WorkspaceViewProvider>
          <Shell>
            <Suspense fallback={<Loading label="Opening…" />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/me" element={<MySpace />} />
              <Route path="/directory" element={<Directory />} />
              <Route path="/employee/:id" element={<Profile />} />
              <Route path="/org" element={<OrgChart />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/offboarding" element={<Offboarding />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/biometric" element={<Biometric />} />
              <Route path="/leave" element={<Leave />} />
              <Route path="/holidays" element={<Holidays />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/payroll" element={<Payroll />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/helpdesk" element={<Helpdesk />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/security" element={<Security />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </Shell>
        </WorkspaceViewProvider>
      </OverlayProvider>
    </ThemeProvider>
  );
}
